import { forwardGeocodeAddress } from '@buxus/shared'
import { useCallback, useState } from 'react'
import { reportOperationalFailure } from '../../platform/errors/webFailureReporting'

type MapAddressSearchModalProps = {
  onClose: () => void
  onResult: (center: [number, number], placeName: string) => void
  onSearchSubmitted?: (payload: {
    result: 'success' | 'not_found' | 'failed' | 'validation_failed'
    queryLength: number
  }) => void
}

export function MapAddressSearchModal({
  onClose,
  onResult,
  onSearchSubmitted,
}: MapAddressSearchModalProps) {
  const [query, setQuery] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = useCallback(async () => {
    const q = query.trim()
    if (!q) {
      setError('Required')
      onSearchSubmitted?.({ result: 'validation_failed', queryLength: 0 })
      return
    }
    setBusy(true)
    setError(null)
    try {
      const hit = await forwardGeocodeAddress(q)
      if (!hit) {
        setError('Not found')
        onSearchSubmitted?.({ result: 'not_found', queryLength: q.length })
        return
      }
      onSearchSubmitted?.({ result: 'success', queryLength: q.length })
      onResult(hit.center, hit.placeName)
      onClose()
    } catch (error) {
      setError('Search failed')
      onSearchSubmitted?.({ result: 'failed', queryLength: q.length })
      reportOperationalFailure(error, {
        context: 'mapAddressSearch',
      })
    } finally {
      setBusy(false)
    }
  }, [onClose, onResult, onSearchSubmitted, query])

  return (
    <div className="map-search-overlay" role="dialog" aria-modal="true" aria-label="Search address">
      <div className="map-search-overlay-backdrop" aria-hidden onClick={onClose} />
      <div className="map-picker-overlay-panel map-search-overlay-panel">
        <div className="map-picker-overlay-header">
          <h2 className="map-picker-overlay-title">Search</h2>
        </div>
        <form
          className="map-search-form"
          onSubmit={(e) => {
            e.preventDefault()
            void submit()
          }}
        >
          <label className="map-search-label" htmlFor="map-address-search">
            Enter address
          </label>
          <input
            id="map-address-search"
            type="search"
            className="map-search-input"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              if (error) setError(null)
            }}
            autoFocus
            autoComplete="street-address"
            disabled={busy}
          />
          {error ? <p className="map-search-error">{error}</p> : null}
          <div className="map-search-actions">
            <button type="button" className="map-search-btn map-search-btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="map-search-btn map-search-btn-primary" disabled={busy}>
              {busy ? 'Searching…' : 'Search'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
